import { useMemo } from "react";
import { isEmpty, noop } from "lodash";
import { useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { SpotlightedHeader } from "@/react/components/LiveStreams/Room/Sidebar/QA/common/SpotlightedHeader";
import { useFrameDocument } from "@/react/hooks/utils/useFrameDocument";
import { ProfileHoverCard } from "@circle-react/components/Profile/HoverCard";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useRailbar } from "@circle-react/hooks/chatsV2/useRailbar";
import { Icon, isIconType } from "@circle-react-shared/Icon";
import { UserImage } from "@circle-react-shared/uikit/UserImage";
import { Avatar, Row } from "@circle-react-uikit/SkeletonLoader/components";
import { getRole } from "../helpers";
import { ContentBody } from "./ContentBody";
import { BottomActions } from "./MessageActions/BottomActions";
import { MessageTimestamp } from "./MessageTimestamp";
import { MessageTimestampLink } from "./MessageTimestamp/MessageTimestampLink";
import { ModeratorTag } from "./ModeratorTag";
import { RichTextUpdateBox } from "./RichTextUpdateBox";
import { RoleIcon } from "./RoleIcon";
import { useSpotlightStatus } from "./hooks/useSpotlightStatus";
import "./styles.scss";

export interface MessageBlockProps {
  chatRoom?: any;
  chatRoomUrl?: string;
  currentParticipant?: any;
  handleSubmit?: (value: any) => void | Promise<void>;
  isEditMessage?: boolean;
  isMessageThread?: boolean;
  isModal?: boolean;
  isParentMessage?: boolean;
  isPinned?: boolean;
  isPinnedMessageAtTop?: boolean;
  isRenderedOnHeader?: boolean;
  isViewOnlyMasquerading: boolean;
  message: any;
  messageBodyClassName?: string;
  participants?: any[];
  roles?: any;
  shouldScrollIntoView?: boolean;
  toggleDeleteConfirmationModal?: () => void;
}

export const MessageBlock = ({
  chatRoom,
  chatRoomUrl,
  currentParticipant = {},
  handleSubmit,
  isEditMessage = false,
  isMessageThread = false,
  isModal = false,
  isParentMessage = false,
  isPinned = false,
  isPinnedMessageAtTop = false,
  isRenderedOnHeader = false,
  message,
  messageBodyClassName,
  participants = [],
  roles = {},
  toggleDeleteConfirmationModal = noop,
  isViewOnlyMasquerading,
  shouldScrollIntoView = false,
}: MessageBlockProps) => {
  const { pathname } = useLocation();
  const memberProfileDrawer = useProfileDrawer();
  const { isInFrame } = useFrameDocument();

  const { chat_room_participant_id: participantId } = message;
  const { isSpotlighted, isCurrentSpotlightedItem } =
    useSpotlightStatus(message);

  const sender = useMemo(
    () => participants.find(participant => participant.id === participantId),
    [participants, participantId],
  );

  const role = useMemo(
    () => getRole(sender?.community_member_id, roles),
    [sender?.community_member_id, roles],
  );

  const isLoadingSender = isEmpty(sender);
  const isMemberProfileAvailable = sender?.user_public_uid;
  const isProfileButtonEnabled = isMemberProfileAvailable && !isInFrame;

  const { community_member_id: currentCommunityMemberId, admin: isAdmin } =
    currentParticipant || {};

  const { isLiveStream } = useRailbar();
  const openProfileDrawer = () => {
    if (isProfileButtonEnabled) {
      void memberProfileDrawer.show({
        memberPublicId: sender.user_public_uid,
        params: {
          shouldOpenInNewTab: isLiveStream,
        },
      });
    }
  };

  const senderName = useMemo(
    () =>
      sender?.id === currentParticipant?.id ? t("messaging.you") : sender?.name,
    [sender, currentParticipant],
  );

  const removedOrLeftBadge = useMemo(
    () => (sender?.deleted_at ? t("messaging.removed_or_left") : ""),
    [sender?.deleted_at],
  );

  const isThreadsListingPage = pathname.endsWith("threads");
  const shouldShowMessageTimestampLink =
    isThreadsListingPage || isRenderedOnHeader;

  return (
    <div
      className={classNames("flex w-full flex-col", {
        "px-4 py-3": !isRenderedOnHeader,
        "px-3 py-1.5": isRenderedOnHeader,
      })}
    >
      {isSpotlighted && (
        <SpotlightedHeader isQueuedForSpotlight={!isCurrentSpotlightedItem} />
      )}
      <div className="flex w-full justify-start">
        <button
          type="button"
          className={classNames("mr-3 mt-1 self-start", {
            "cursor-default": !isMemberProfileAvailable,
          })}
          aria-label={t("messaging.open_member_profile_button_aria_label", {
            name: sender?.name,
          })}
          disabled={!isProfileButtonEnabled}
          onClick={openProfileDrawer}
        >
          {isLoadingSender ? (
            <Avatar />
          ) : (
            <ProfileHoverCard
              communityMemberId={sender.community_member_id}
              name={sender.name}
              publicUid={sender.user_public_uid}
              headline={sender.headline}
              avatarURL={sender.avatar_url}
              disabled={!isProfileButtonEnabled}
            >
              <UserImage src={sender.avatar_url} name={sender.name} size="9" />
            </ProfileHoverCard>
          )}
        </button>
        <div
          className={classNames("w-full overflow-x-auto", messageBodyClassName)}
        >
          <div className="mb-px flex items-center gap-1.5">
            {isPinned && (
              <Icon
                type="14-pin"
                aria-label={t("messaging.pinned_message")}
                aria-hidden={false}
                size={14}
                className="text-darkest !w-3.5"
              />
            )}
            <button
              type="button"
              className={classNames(
                "text-darkest truncate text-left text-sm font-semibold",
                {
                  "cursor-default": !isMemberProfileAvailable,
                },
              )}
              disabled={!isProfileButtonEnabled}
              onClick={openProfileDrawer}
              data-testid="number-of-replies"
            >
              {isLoadingSender ? (
                <Row md />
              ) : (
                <>
                  {senderName}{" "}
                  <span className="font-normal">{removedOrLeftBadge}</span>
                </>
              )}
            </button>
            {role && isIconType(role) && <RoleIcon role={role} />}
            <ModeratorTag
              chatRoomKind={chatRoom?.chat_room_kind}
              sender={sender}
            />
            {shouldShowMessageTimestampLink ? (
              <MessageTimestampLink
                chatRoom={chatRoom}
                message={message}
                isParentMessage={isParentMessage}
                isMessageThread={isMessageThread}
                isRenderedOnHeader={isRenderedOnHeader}
              />
            ) : (
              <MessageTimestamp
                message={message}
                chatRoomUrl={chatRoomUrl}
                isParentMessage={isParentMessage}
                isMessageThread={isMessageThread}
              />
            )}
          </div>
          {!isModal && isEditMessage ? (
            <RichTextUpdateBox
              message={message}
              onSubmit={handleSubmit}
              onDelete={toggleDeleteConfirmationModal}
              chatRoom={chatRoom}
              isRenderedOnHeader={isRenderedOnHeader}
            />
          ) : (
            <ContentBody
              message={message}
              isPinnedMessageAtTop={isPinnedMessageAtTop}
              shouldScrollIntoView={shouldScrollIntoView}
            />
          )}
          <BottomActions
            message={message}
            chatRoom={chatRoom}
            currentCommunityMemberId={currentCommunityMemberId}
            isSpotlighted={isSpotlighted}
            isCurrentSpotlightedItem={isCurrentSpotlightedItem}
            isAdmin={isAdmin}
            isMessageThread={isMessageThread}
            isRenderedOnHeader={isRenderedOnHeader}
            isViewOnlyMasquerading={isViewOnlyMasquerading}
          />
        </div>
      </div>
    </div>
  );
};
